import { HousingApplicationStatus } from "~/api";
import { housingApplicationStatusDisplayNamesHMBSite } from "./enumDisplayNames";

export interface AllowedStatus {
  value: string;
  label: string;
  disabled: boolean;
  isDefault: boolean;
}

/**
 * Active statuses in C# enum declaration order (indices 0–7).
 * This order defines the progression threshold: statuses beyond
 * the threshold index are disabled.
 */
const STATUS_PROGRESSION: HousingApplicationStatus[] = [
  HousingApplicationStatus.DRAFT,
  HousingApplicationStatus.NEW,
  HousingApplicationStatus.IN_REVIEW,
  HousingApplicationStatus.WAITLIST,
  // HousingApplicationStatus.ON_HOLD,
  HousingApplicationStatus.CANCELLED,
  // HousingApplicationStatus.LOCKED_INTERVIEWING,
  // HousingApplicationStatus.LOCKED_WITH_OFFER,
  HousingApplicationStatus.HOUSED,
];

/**
 * Statuses that aren't shown as options but should resolve to a
 * logical equivalent in STATUS_PROGRESSION for threshold/default purposes.
 */
const STATUS_ALIASES: Partial<
  Record<HousingApplicationStatus, HousingApplicationStatus>
> = {
  [HousingApplicationStatus.LOCKED_INTERVIEWING]:
    HousingApplicationStatus.WAITLIST,
  [HousingApplicationStatus.LOCKED_WITH_OFFER]:
    HousingApplicationStatus.WAITLIST,
};

/**
 * Statuses that are always enabled regardless of progression threshold.
 */
const ALWAYS_ENABLED: Set<HousingApplicationStatus> = new Set([
  HousingApplicationStatus.CANCELLED,
]);

/** Normalize a status string for case/space/hyphen-insensitive matching. */
const normalize = (s: string): string => s.replace(/[\s-]/g, "").toLowerCase();

/**
 * Compute allowed statuses for the ApplicationStatusSelect dropdown.
 *
 * Uses the frontend STATUS_PROGRESSION order to:
 * 1. Resolve the effective status from statusOnCreate, mapping aliases
 *    (e.g. LOCKED_INTERVIEWING → WAITLIST) for statuses not in the progression.
 * 2. Find its index in STATUS_PROGRESSION.
 * 3. Disable statuses whose index is greater than the threshold,
 *    unless the status is in ALWAYS_ENABLED.
 * 4. Mark the resolved status as isDefault.
 * 5. Populate labels from housingApplicationStatusDisplayNamesHMBSite.
 */
export function computeAllowedStatuses(
  statusOnCreate: string | null,
): AllowedStatus[] {
  const effectiveStatus = statusOnCreate || null;

  let thresholdIndex: number | null = null;
  let resolvedStatus: HousingApplicationStatus | null = null;

  if (effectiveStatus) {
    const normalizedEffective = normalize(effectiveStatus);

    const matched =
      STATUS_PROGRESSION.find((s) => normalize(s) === normalizedEffective) ??
      (Object.keys(STATUS_ALIASES).find(
        (s) => normalize(s) === normalizedEffective,
      ) as HousingApplicationStatus | undefined);

    if (matched) {
      resolvedStatus =
        STATUS_ALIASES[matched as HousingApplicationStatus] ??
        (matched as HousingApplicationStatus);
      const idx = STATUS_PROGRESSION.indexOf(resolvedStatus);
      if (idx !== -1) thresholdIndex = idx;
    }
  }

  return STATUS_PROGRESSION.map((status, i) => {
    const label = housingApplicationStatusDisplayNamesHMBSite[status]!;
    return {
      value: status as string,
      label,
      disabled:
        thresholdIndex !== null && !ALWAYS_ENABLED.has(status)
          ? i > thresholdIndex
          : false,
      isDefault: resolvedStatus !== null && status === resolvedStatus,
    };
  });
}
